import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatRand, formatApiErrorDetail, mediaUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CreditCard, Lock, CheckCircle2 } from "lucide-react";

export default function Checkout() {
  const { items, total, clear } = useCart();
  const navigate = useNavigate();
  const [placing, setPlacing] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [payConfigured, setPayConfigured] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    address: "",
    city: "",
    postal_code: "",
    notes: "",
  });

  useEffect(() => {
    api.get("/payments/addpay/config").then((r) => setPayConfigured(!!r.data.configured)).catch(() => {});
  }, []);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const placeOrder = async (e) => {
    e.preventDefault();
    if (items.length === 0) {
      toast.error("Your bag is empty");
      return;
    }
    setPlacing(true);
    try {
      const payload = {
        ...form,
        items: items.map((i) => ({
          product_id: i.product_id,
          name: i.name,
          size: i.size,
          colour: i.colour,
          price: i.price,
          quantity: i.quantity,
          image: i.image,
        })),
      };
      const { data } = await api.post("/orders", payload);
      clear();
      // Try to hand off to AddPay. If not configured yet, the order stays pending.
      try {
        const pay = await api.post(`/payments/addpay/checkout/${data.id}`);
        if (pay.data?.configured && pay.data?.pay_url) {
          window.location.assign(pay.data.pay_url);
          return;
        }
      } catch (payErr) {
        // Non-fatal: order is placed; show pending confirmation below.
        console.warn("AddPay not available:", payErr?.response?.data?.detail);
      }
      setConfirmed(data);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not place order");
    } finally {
      setPlacing(false);
    }
  };

  if (confirmed)
    return (
      <div data-testid="order-confirmation" className="mx-auto max-w-2xl px-6 py-16 text-center">
        <div className="w-20 h-20 rounded-full bg-emerald-50 flex items-center justify-center mx-auto">
          <CheckCircle2 className="w-10 h-10 text-emerald-500" />
        </div>
        <h1 className="font-display text-3xl font-bold text-purple-950 mt-6">Order Confirmed! 🦋</h1>
        <p className="text-muted-foreground mt-3">
          Thank you for shopping with Ciara's Kidz. Your order number is
        </p>
        <p data-testid="order-number" className="text-2xl font-bold brand-gradient-text mt-2">{confirmed.order_number}</p>
        <div className="mt-6 bg-white border border-purple-100 rounded-2xl p-6 text-left">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-muted-foreground">Total</span>
            <span className="font-bold">{formatRand(confirmed.total)}</span>
          </div>
          <div className="flex justify-between text-sm mb-2">
            <span className="text-muted-foreground">Status</span>
            <span className="font-semibold text-amber-600">{confirmed.status}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Payment</span>
            <span className="font-medium text-purple-700">{confirmed.payment_status}</span>
          </div>
        </div>
        <div className="mt-8 flex gap-3 justify-center">
          <button onClick={() => navigate("/account")} className="px-6 py-3 rounded-full brand-gradient text-white font-semibold">
            View My Orders
          </button>
          <button onClick={() => navigate("/shop")} className="px-6 py-3 rounded-full bg-white border border-purple-200 font-semibold text-purple-800">
            Continue Shopping
          </button>
        </div>
      </div>
    );

  return (
    <div data-testid="checkout-page" className="mx-auto max-w-6xl px-6 py-10">
      <h1 className="font-display text-3xl sm:text-4xl font-bold text-purple-950 mb-8">Checkout</h1>
      <div className="grid lg:grid-cols-3 gap-8">
        <form onSubmit={placeOrder} className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-2xl border border-purple-100 p-6">
            <h2 className="font-semibold text-lg mb-4">Delivery Details</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label>Full Name</Label>
                <Input data-testid="checkout-name" required value={form.full_name} onChange={upd("full_name")} className="mt-1.5 bg-white" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input data-testid="checkout-phone" required value={form.phone} onChange={upd("phone")} className="mt-1.5 bg-white" />
              </div>
              <div>
                <Label>Postal Code</Label>
                <Input data-testid="checkout-postal" required value={form.postal_code} onChange={upd("postal_code")} className="mt-1.5 bg-white" />
              </div>
              <div className="sm:col-span-2">
                <Label>Street Address</Label>
                <Input data-testid="checkout-address" required value={form.address} onChange={upd("address")} className="mt-1.5 bg-white" />
              </div>
              <div className="sm:col-span-2">
                <Label>City / Town</Label>
                <Input data-testid="checkout-city" required value={form.city} onChange={upd("city")} className="mt-1.5 bg-white" />
              </div>
              <div className="sm:col-span-2">
                <Label>Order Notes (optional)</Label>
                <Textarea data-testid="checkout-notes" value={form.notes} onChange={upd("notes")} className="mt-1.5 bg-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-purple-100 p-6">
            <h2 className="font-semibold text-lg mb-3 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-fuchsia-500" /> Payment
            </h2>
            <div className={`rounded-xl p-4 text-sm flex items-start gap-2 border ${payConfigured ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-800"}`} data-testid="payment-notice">
              <Lock className="w-4 h-4 mt-0.5 shrink-0" />
              {payConfigured ? (
                <span>
                  Secure card payment powered by <strong>AddPay</strong>. When you place your order you'll be redirected
                  to AddPay's secure page; if it's momentarily unavailable, your order is safely saved as Pending.
                </span>
              ) : (
                <span>
                  Secure payment via <strong>AddPay</strong> is being finalised. For now your order is placed and marked
                  <strong> Pending Payment</strong> — our team will contact you to arrange payment.
                </span>
              )}
            </div>
          </div>

          <button
            type="submit"
            data-testid="checkout-submit-button"
            disabled={placing || items.length === 0}
            className="w-full py-4 rounded-full brand-gradient text-white font-semibold text-lg hover:opacity-90 transition disabled:opacity-50"
          >
            {placing ? "Placing order..." : `Place Order · ${formatRand(total)}`}
          </button>
        </form>

        {/* Summary */}
        <div>
          <div className="bg-white rounded-2xl border border-purple-100 p-6 sticky top-24">
            <h2 className="font-semibold text-lg mb-4">Order Summary</h2>
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {items.map((i) => (
                <div key={i.key} className="flex gap-3">
                  <img src={mediaUrl(i.image)} alt={i.name} className="w-14 h-16 rounded-lg object-contain bg-purple-50 p-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium line-clamp-1">{i.name}</p>
                    <p className="text-xs text-muted-foreground">{i.colour && `${i.colour} · `}Size {i.size} · Qty {i.quantity}</p>
                    <p className="text-sm font-semibold">{formatRand(i.price * i.quantity)}</p>
                  </div>
                </div>
              ))}
              {items.length === 0 && <p className="text-sm text-muted-foreground">Your bag is empty.</p>}
            </div>
            <div className="border-t border-purple-100 mt-4 pt-4 flex justify-between font-bold text-lg">
              <span>Total</span>
              <span className="brand-gradient-text">{formatRand(total)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
